import { MemberContactsType } from '@/api/members'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
    memberContactsQueryOptions,
    useUpdateMemberContactsMutation,
} from '../../api/memberQueryOptions'

import yup from '@/yup/yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'

import MemberContactForm from '@/components/form/member/MemberContactForm'
import LoadProgress from '@/components/LoadProgress'
import { DevTool } from '@hookform/devtools'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import React from 'react'

const schema = yup
    .object({
        uuid: yup.string().required(),
        contact1Type: yup.string().max(30).label('Typ 1'),
        contact1Name: yup.string().max(255).label('Name 1'),
        contact1Address: yup.string().max(255).label('Adresse 1'),
        contact1Email: yup.string().max(255).label('Email 1'),
        contact1Phone: yup.string().max(255).label('Telefon 1'),
        contact1Profession: yup.string().max(255).label('Beruf 1'),
        contact1Related: yup.boolean().label('Kontakt 1 zugehörig'),
        contact2Type: yup.string().max(30).label('Typ 2'),
        contact2Name: yup.string().max(255).label('Name 2'),
        contact2Address: yup.string().max(255).label('Adresse 2'),
        contact2Email: yup.string().max(255).label('Email 2'),
        contact2Phone: yup.string().max(255).label('Telefon 2'),
        contact2Profession: yup.string().max(255).label('Beruf 2'),
        contact2Related: yup.boolean().label('Kontakt 2 zugehörig'),
        memContact1Type: yup.string().max(30).label('Typ 1'),
        memContact2Type: yup.string().max(30).label('Typ 2'),
        permissions: yup.array(),
    })
    .required()

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/contacts',
)({
    component: MemberComponent,
})

function MemberComponent() {
    const params = Route.useParams()
    const memberContactsQuery = useSuspenseQuery(
        memberContactsQueryOptions(params.memberUuid),
    )
    const memberContacts = memberContactsQuery.data
    const updateMemberMutation = useUpdateMemberContactsMutation(
        params.memberUuid,
    )

    const { control, register, handleSubmit } = useForm<MemberContactsType>({
        resolver: yupResolver<MemberContactsType, any, any>(schema),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const watch = useWatch({ control, name: ['contact1Type', 'contact2Type'] })

    const onSubmit = (data: MemberContactsType) => {
        updateMemberMutation.mutate(
            { ...memberContacts, ...data },
            {
                onError(error) {
                    enqueueSnackbar(t('error.message', { message: error.message }), {
                        variant: 'error',
                    })
                },
                onSuccess() {
                    enqueueSnackbar(
                        t('action.changed', { entity: t('entity.memberContacts') }),
                        { variant: 'success' },
                    )
                },
            },
        )
    }

    const writeable = memberContacts.permissions
        ? memberContacts.permissions.some((perm) => perm === 'write')
        : false

    const contact1Writeable = writeable && memberContacts.contact1Related !== true
    const contact2Writeable = writeable && memberContacts.contact2Related !== true


    const contact1TypeLabel =
        memberContacts.contact1Related !== true
            ? memberContacts.contact1Type && memberContacts.contact1Type.length > 0
                ? memberContacts.contact1Type
                : ''
            : t(
                'enum.memberRelation.type.' + memberContacts.contact1Type,
                memberContacts.contact1Type as string,
            )
    const [contact1TypeLabelState, setContact1TypeLabelState] = React.useState(contact1TypeLabel)
    const contact2TypeLabel =
        memberContacts.contact2Related !== true
            ? memberContacts.contact2Type && memberContacts.contact2Type.length > 0
                ? memberContacts.contact2Type
                : ''
            : t(
                'enum.memberRelation.type.' + memberContacts.contact2Type,
                memberContacts.contact1Type as string,
            )
    const [contact2TypeLabelState, setContact2TypeLabelState] = React.useState(contact2TypeLabel)

    React.useEffect(() => {
        setContact1TypeLabelState(watch[0] || '')
        setContact2TypeLabelState(watch[1] || '')
    }, [watch])


    if (memberContactsQuery.isLoading || memberContactsQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <form onBlur={handleSubmit(onSubmit)}>
            <input
                type="hidden"
                {...register('uuid')}
                defaultValue={memberContacts.uuid}
            />

            <MemberContactForm
                register={register}
                index={1}
                memberContacts={memberContacts}
                writeable={contact1Writeable}
                label={contact1TypeLabelState}
            />
            <MemberContactForm
                register={register}
                index={2}
                memberContacts={memberContacts}
                writeable={contact2Writeable}
                label={contact2TypeLabelState}
            />
            {import.meta.env.VITE_DEBUG === 'true' &&
                <DevTool control={control} />
            }
        </form>
    )
}
